///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2025
//                           RXO Inc.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of RXO Inc. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import messages from "./messages";
import {
  controls,
  equipments,
  patterns,
  fscTypes,
  fscTypesIds,
  awardType,
  awardRank,
  cellUnit,
  rfqStatusTypes,
  division,
  confidenceTypeColorCodes
} from "./types";
import { getShipperDistanceStyle } from "./getShipperDistanceStyle";
import tlColumnConstants from "./tlColumnConstants";
import {
  areNumbers,
  calculateBaseRpm,
  calculateCpm,
  calculateFlatRate,
  calculateRpm,
  calculateTotalCost,
  numberParse,
  round,
  roundForNullableField,
  numberParseForEmptyValue,
  formatDate,
  getShiperMiles
} from "./laneCalculations";
import getTTTCostStyle from "./getTTTCostStyle";
import formatHasMatches from "./formatHasMatches";

const {
  LANE_HAS_MATCHES,
  SHIPPER_LANE_ID,
  TOTAL_RATE,
  RATE_PER_UNIT,
  FLAT_RATE,
  BASE_RATE_PER_UNIT,
  FUEL_RATE,
  ORIGIN_CITY,
  ORIGIN_STATE,
  ORIGIN_POSTAL_CODE,
  ORIGIN_COUNTRY,
  DESTINATION_CITY,
  DESTINATION_STATE,
  DESTINATION_POSTAL_CODE,
  DESTINATION_COUNTRY,
  EQUIPMENT_TYPE,
  SHIPPER_DISTANCE,
  COYOTE_DISTANCE,
  PREVIOUS_TTT_COST,
  TTT_COST,
  PRICING_MODEL,
  COST_PER_UNIT,
  TOTAL_COST,
  MARGIN,
  MARGIN_PERCENTAGE,
  REGION,
  VOLUME,
  ADDITIONAL_FEE,
  ORIGIN_NOTE,
  DESTINATION_NOTE,
  BID_NOTE,
  LAST_EDITED_USER_NAME,
  EDITED,
  HAZMAT,
  ORIGIN_DROP_TRAILER,
  DESTINATION_DROP_TRAILER,
  PERMIT,
  TEAM,
  HRHV,
  TARGET_RATE,
  MINIMUM_RATE,
  CAPACITY_SOURCE,
  INCUMBENT_RATE,
  FUEL_SURCHARGE,
  AWARD_RATE,
  AWARD_TYPE,
  AWARD_VOLUME,
  AWARD_RANK,
  FUEL_SURCHARGE_TYPE,
  MX_COST,
  BORDER_CROSSING_FEE,
  TRANSLOAD_FEE,
  BID_START_DATE,
  BID_END_DATE,
  SHIPPER_NAME,
  BID_NAME,
  BID_STATUS,
  MX_MILES,
  BORDER_CROSSING_CITY,
  BORDER_CROSSING_STATE,
  BORDER_CROSSING_COUNTRY,
  PRICING_CONFIDENCE,
  STRATEGIC_PERIOD_TTT_COST,
  STRATEGIC_QUANTILE_TTT_COST,
  STRATEGIC_QUANTILE
} = tlColumnConstants;

export { MARGIN, MARGIN_PERCENTAGE };

export const tlColumns = [
  {
    field: LANE_HAS_MATCHES,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.matches.defaultMessage,
    center: true,
    control: controls.laneMatch,
    resize: true,
    filter: true,
    format: formatHasMatches,
    isDraggable: false,
    sortable: false,
    columnChooserLabel: messages.matches.defaultMessage,
    requiredColumn: true
  },
  {
    field: SHIPPER_LANE_ID,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.laneId.defaultMessage,
    left: true,
    control: controls.none,
    width: "medium",
    filter: true,
    requiredColumn: true
  },
  {
    field: EDITED,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.edited.defaultMessage,
    control: controls.tag,
    center: true,
    disabled: true,
    width: "tiny",
    laneMatchOrder: 17,
    tagLabel: [
      { value: true, label: messages.edited.defaultMessage },
      { value: false, label: "" }
    ]
  },
  {
    field: TOTAL_RATE,
    cellUnit: cellUnit.dollar,
    unitLeft: true,
    header: messages.totRate.defaultMessage,
    right: true,
    control: controls.textInput,
    width: "medium",
    pattern: patterns.intSixFractionalTwo,
    parse: numberParse,
    calculate: (lane, changedBy) => {
      if ([FLAT_RATE, BASE_RATE_PER_UNIT].some(i => i === changedBy)) {
        if (areNumbers(lane, tlColumnConstants, [FLAT_RATE, FUEL_RATE])) {
          lane[TOTAL_RATE] = lane[FLAT_RATE] + lane[FUEL_RATE];
        }
      } else if (changedBy === RATE_PER_UNIT) {
        if (areNumbers(lane, tlColumnConstants, [RATE_PER_UNIT])) {
          lane[TOTAL_RATE] =
            lane[RATE_PER_UNIT] * getShiperMiles(lane, tlColumnConstants);
        }
      } else {
        if (
          areNumbers(lane, tlColumnConstants, [
            TOTAL_COST,
            MARGIN,
            ADDITIONAL_FEE,
            MX_COST,
            BORDER_CROSSING_FEE,
            TRANSLOAD_FEE
          ])
        ) {
          lane[TOTAL_RATE] =
            lane[TOTAL_COST] +
            lane[MARGIN] +
            lane[ADDITIONAL_FEE] +
            lane[MX_COST] +
            lane[BORDER_CROSSING_FEE] +
            lane[TRANSLOAD_FEE];
        }
      }
    },
    format: round,
    effects: lane =>
      lane[FUEL_SURCHARGE_TYPE] === fscTypesIds.percentOfRate
        ? [
            MARGIN,
            MARGIN_PERCENTAGE,
            FLAT_RATE,
            RATE_PER_UNIT,
            BASE_RATE_PER_UNIT,
            FUEL_SURCHARGE
          ]
        : [
            MARGIN,
            MARGIN_PERCENTAGE,
            FLAT_RATE,
            RATE_PER_UNIT,
            BASE_RATE_PER_UNIT
          ]
  },
  {
    field: RATE_PER_UNIT,
    cellUnit: cellUnit.dollar,
    unitLeft: true,
    header: messages.rpm.defaultMessage,
    right: true,
    control: controls.textInput,
    width: "tiny",
    pattern: patterns.intTwoFractionalTwo,
    parse: numberParse,
    calculate: (lane, changedBy) =>
      calculateRpm({ lane, changedBy, constants: tlColumnConstants }),
    format: round,
    effects: () => [
      TOTAL_RATE,
      TOTAL_COST,
      MARGIN,
      MARGIN_PERCENTAGE,
      FUEL_RATE,
      FLAT_RATE,
      BASE_RATE_PER_UNIT
    ]
  },
  {
    field: FLAT_RATE,
    cellUnit: cellUnit.dollar,
    unitLeft: true,
    header: messages.flatRate.defaultMessage,
    right: true,
    control: controls.textInput,
    width: "medium",
    pattern: patterns.intSixFractionalTwo,
    parse: numberParse,
    calculate: (lane, changedBy) =>
      calculateFlatRate({ lane, changedBy, constants: tlColumnConstants }),
    format: round,
    effects: () => [
      TOTAL_RATE,
      MARGIN,
      MARGIN_PERCENTAGE,
      BASE_RATE_PER_UNIT,
      RATE_PER_UNIT,
      FUEL_RATE
    ]
  },
  {
    field: BASE_RATE_PER_UNIT,
    cellUnit: cellUnit.dollar,
    unitLeft: true,
    header: messages.baseRpm.defaultMessage,
    right: true,
    control: controls.textInput,
    width: "medium",
    pattern: patterns.intTwoFractionalTwo,
    parse: numberParse,
    calculate: (lane, changedBy) =>
      calculateBaseRpm({ lane, changedBy, constants: tlColumnConstants }),
    format: round,
    effects: () => [
      FUEL_RATE,
      TOTAL_COST,
      FLAT_RATE,
      TOTAL_RATE,
      MARGIN,
      MARGIN_PERCENTAGE,
      RATE_PER_UNIT,
      FUEL_SURCHARGE
    ]
  },
  {
    field: FUEL_RATE,
    cellUnit: cellUnit.dollar,
    unitLeft: true,
    header: messages.fuelRate.defaultMessage,
    right: true,
    control: controls.textInput,
    width: "medium",
    pattern: patterns.intSixFractionalTwo,
    parse: numberParse,
    calculate: lane => {
      if (lane[FUEL_SURCHARGE_TYPE] === fscTypesIds.dollarPerMile) {
        if (
          areNumbers(lane, tlColumnConstants, [
            FUEL_SURCHARGE,
            SHIPPER_DISTANCE
          ])
        ) {
          lane[FUEL_RATE] =
            lane[FUEL_SURCHARGE] * getShiperMiles(lane, tlColumnConstants);
        }
      } else if (lane[FUEL_SURCHARGE_TYPE] === fscTypesIds.percentOfRate) {
        if (
          areNumbers(lane, tlColumnConstants, [
            RATE_PER_UNIT,
            FUEL_SURCHARGE,
            SHIPPER_DISTANCE
          ])
        ) {
          lane[FUEL_RATE] =
            (lane[RATE_PER_UNIT] -
              lane[RATE_PER_UNIT] / (1 + lane[FUEL_SURCHARGE] / 100)) *
            getShiperMiles(lane, tlColumnConstants);
        }
      } else if (lane[FUEL_SURCHARGE_TYPE] === fscTypesIds.breakthrough) {
        if (areNumbers(lane, tlColumnConstants, [FUEL_SURCHARGE])) {
          lane[FUEL_RATE] = lane[FUEL_SURCHARGE];
        }
      }
    },
    format: round,
    effects: () => [
      TOTAL_COST,
      TOTAL_RATE,
      RATE_PER_UNIT,
      FUEL_SURCHARGE,
      MARGIN,
      MARGIN_PERCENTAGE,
      FLAT_RATE,
      BASE_RATE_PER_UNIT
    ]
  },
  {
    field: ORIGIN_CITY,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.origCity.defaultMessage,
    control: controls.none,
    width: "",
    filter: true,
    left: true,
    laneMatchOrder: 1
  },
  {
    field: ORIGIN_STATE,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.origSt.defaultMessage,
    control: controls.none,
    width: "",
    filter: true,
    left: true,
    laneMatchOrder: 2
  },
  {
    field: ORIGIN_POSTAL_CODE,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.origPostal.defaultMessage,
    control: controls.none,
    width: "",
    filter: true,
    left: true
  },
  {
    field: ORIGIN_COUNTRY,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.origCountry.defaultMessage,
    control: controls.none,
    width: "",
    filter: true,
    left: true
  },
  {
    field: DESTINATION_CITY,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.destCity.defaultMessage,
    control: controls.none,
    width: "",
    filter: true,
    left: true,
    laneMatchOrder: 3
  },
  {
    field: DESTINATION_STATE,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.destSt.defaultMessage,
    control: controls.none,
    width: "",
    filter: true,
    left: true,
    laneMatchOrder: 4
  },
  {
    field: DESTINATION_POSTAL_CODE,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.destPostal.defaultMessage,
    control: controls.none,
    width: "medium",
    filter: true,
    left: true
  },
  {
    field: DESTINATION_COUNTRY,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.destCountry.defaultMessage,
    control: controls.none,
    width: "medium",
    filter: true,
    left: true
  },
  {
    field: EQUIPMENT_TYPE,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.eq.defaultMessage,
    control: controls.none,
    width: "",
    filter: true,
    left: true,
    format: value => equipments[value],
    laneMatchOrder: 11
  },
  {
    field: SHIPPER_DISTANCE,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.shipMi.defaultMessage,
    right: true,
    control: controls.textInput,
    width: "medium",
    pattern: patterns.intFourFractionalOne,
    parse: numberParseForEmptyValue,
    format: roundForNullableField,
    effects: () => [
      TOTAL_COST,
      TOTAL_RATE,
      RATE_PER_UNIT,
      FUEL_RATE,
      MARGIN,
      MARGIN_PERCENTAGE,
      FLAT_RATE,
      BASE_RATE_PER_UNIT
    ],
    rangeFilter: true,
    style: lane =>
      getShipperDistanceStyle(lane[SHIPPER_DISTANCE], lane[COYOTE_DISTANCE])
  },
  {
    field: COYOTE_DISTANCE,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.coyoteMi.defaultMessage,
    right: true,
    control: controls.textInput,
    width: "medium",
    pattern: patterns.minOneIntFourFractionalOne,
    parse: numberParse,
    format: round,
    effects: () => [
      TOTAL_COST,
      MARGIN,
      TOTAL_RATE,
      RATE_PER_UNIT,
      FUEL_RATE,
      FLAT_RATE,
      BASE_RATE_PER_UNIT
    ],
    rangeFilter: true,
    laneMatchOrder: 5,
    alternateHeader: messages.miles.defaultMessage
  },
  {
    field: PREVIOUS_TTT_COST,
    cellUnit: cellUnit.dollar,
    unitLeft: true,
    header: messages.prevContractTTTCost.defaultMessage,
    control: controls.none,
    width: "medium",
    right: true,
    format: roundForNullableField
  },
  {
    field: TTT_COST,
    cellUnit: cellUnit.dollar,
    unitLeft: true,
    header: messages.contractTTTCost.defaultMessage,
    control: controls.none,
    width: "medium",
    right: true,
    filter: true,
    filterField: PRICING_CONFIDENCE,
    style: (lane, role) => getTTTCostStyle(lane.pricingConfidence, role)
  },
  {
    field: STRATEGIC_PERIOD_TTT_COST,
    cellUnit: cellUnit.dollar,
    unitLeft: true,
    header: messages.strategicPeriodTTTCost.defaultMessage,
    control: controls.none,
    width: "medium",
    right: true,
    format: roundForNullableField,
    laneMatchOrder: 16
  },
  {
    field: STRATEGIC_QUANTILE_TTT_COST,
    cellUnit: cellUnit.dollar,
    unitLeft: true,
    header: messages.strategicTTTCost.defaultMessage,
    control: controls.none,
    width: "medium",
    right: true,
    format: roundForNullableField
  },
  {
    field: STRATEGIC_QUANTILE,
    cellUnit: cellUnit.percent,
    unitLeft: false,
    header: messages.strategicQuantile.defaultMessage,
    control: controls.textInput,
    width: "medium",
    right: true,
    pattern: patterns.zeroToHundred,
    parse: (value, minValue, maxValue) => {
      let parsedValue = numberParseForEmptyValue(value);
      if (parsedValue.isValid) {
        if (parsedValue.value >= minValue && parsedValue.value <= maxValue) {
          return parsedValue;
        } else {
          return { value: null, isValid: false };
        }
      } else {
        return parsedValue;
      }
    },
    format: roundForNullableField,
    intPercent: true,
    validationBetweenMinMaxValues: true,
    minValue: 0,
    maxValue: 100
  },
  {
    field: PRICING_MODEL,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.pricingModel.defaultMessage,
    control: controls.none,
    width: "medium",
    filter: true,
    left: true
  },
  {
    field: COST_PER_UNIT,
    cellUnit: cellUnit.dollar,
    unitLeft: true,
    header: messages.cpm.defaultMessage,
    right: true,
    width: "tiny",
    control: controls.textInput,
    pattern: patterns.intTwoFractionalTwo,
    parse: numberParse,
    calculate: (lane, changedBy) =>
      calculateCpm({ lane, changedBy, constants: tlColumnConstants }),
    format: round,
    effects: () => [
      TOTAL_COST,
      MARGIN,
      TOTAL_RATE,
      RATE_PER_UNIT,
      FUEL_RATE,
      FLAT_RATE,
      BASE_RATE_PER_UNIT
    ],
    laneMatchOrder: 13
  },
  {
    field: TOTAL_COST,
    cellUnit: cellUnit.dollar,
    unitLeft: true,
    header: messages.totCost.defaultMessage,
    right: true,
    width: "medium",
    control: controls.textInput,
    pattern: patterns.intSixFractionalTwo,
    parse: numberParse,
    calculate: (lane, changedBy) =>
      calculateTotalCost({ lane, changedBy, constants: tlColumnConstants }),
    format: round,
    effects: () => [
      COST_PER_UNIT,
      MARGIN,
      TOTAL_RATE,
      FUEL_RATE,
      FLAT_RATE,
      RATE_PER_UNIT,
      BASE_RATE_PER_UNIT
    ],
    laneMatchOrder: 14
  },
  {
    field: MARGIN,
    cellUnit: cellUnit.dollar,
    unitLeft: true,
    header: messages.margin.defaultMessage,
    right: true,
    control: controls.textInput,
    width: "medium",
    parse: numberParse,
    calculate: (lane, changedBy) => {
      if (
        changedBy === TOTAL_COST ||
        changedBy === COST_PER_UNIT ||
        changedBy === COYOTE_DISTANCE ||
        changedBy === ADDITIONAL_FEE ||
        changedBy === MARGIN_PERCENTAGE ||
        changedBy === MX_COST ||
        changedBy === BORDER_CROSSING_FEE ||
        changedBy === TRANSLOAD_FEE
      ) {
        if (
          areNumbers(lane, tlColumnConstants, [
            TOTAL_COST,
            MARGIN_PERCENTAGE,
            ADDITIONAL_FEE,
            MX_COST
          ])
        ) {
          lane[MARGIN] =
            (lane[TOTAL_COST] + lane[ADDITIONAL_FEE] + lane[MX_COST]) /
              (1 - lane[MARGIN_PERCENTAGE] / 100) -
            (lane[TOTAL_COST] + lane[ADDITIONAL_FEE] + lane[MX_COST]);
        }
      } else {
        if (
          areNumbers(lane, tlColumnConstants, [
            TOTAL_RATE,
            ADDITIONAL_FEE,
            TOTAL_COST,
            MX_COST,
            BORDER_CROSSING_FEE,
            TRANSLOAD_FEE
          ])
        ) {
          lane[MARGIN] =
            lane[TOTAL_RATE] -
            lane[BORDER_CROSSING_FEE] -
            lane[TRANSLOAD_FEE] -
            lane[TOTAL_COST] -
            lane[ADDITIONAL_FEE] -
            lane[MX_COST];
        }
      }
    },
    pattern: patterns.intSixFractionalTwoWithNegative,
    format: round,
    effects: () => [
      TOTAL_COST,
      TOTAL_RATE,
      RATE_PER_UNIT,
      FUEL_RATE,
      FLAT_RATE,
      BASE_RATE_PER_UNIT,
      MARGIN_PERCENTAGE
    ],
    laneMatchOrder: 15
  },
  {
    field: MARGIN_PERCENTAGE,
    cellUnit: cellUnit.percent,
    unitLeft: false,
    header: messages.marginPct.defaultMessage,
    right: true,
    control: controls.textInput,
    width: "medium",
    pattern: patterns.intTwoFractionalTwo,
    parse: numberParse,
    calculate: lane => {
      if (
        areNumbers(lane, tlColumnConstants, [
          MARGIN,
          TOTAL_RATE,
          TOTAL_COST,
          ADDITIONAL_FEE,
          MX_COST,
          BORDER_CROSSING_FEE,
          TRANSLOAD_FEE
        ])
      ) {
        if (
          lane[ADDITIONAL_FEE] +
            lane[MARGIN] +
            lane[TOTAL_COST] +
            lane[MX_COST] +
            lane[BORDER_CROSSING_FEE] +
            lane[TRANSLOAD_FEE] ===
          0
        ) {
          lane[MARGIN_PERCENTAGE] = 0;
        } else {
          lane[MARGIN_PERCENTAGE] =
            (100 * lane[MARGIN]) /
            (lane[MARGIN] +
              lane[TOTAL_COST] +
              lane[ADDITIONAL_FEE] +
              lane[MX_COST] +
              lane[BORDER_CROSSING_FEE] +
              lane[TRANSLOAD_FEE]);
        }
      }
    },
    format: round,
    effects: () => [
      TOTAL_COST,
      MARGIN,
      TOTAL_RATE,
      RATE_PER_UNIT,
      FUEL_RATE,
      FLAT_RATE,
      BASE_RATE_PER_UNIT
    ],
  },
  {
    field: REGION,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.region.defaultMessage,
    control: controls.none,
    width: "medium",
    filter: true,
    left: true
  },
  {
    field: VOLUME,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.vol.defaultMessage,
    right: true,
    control: controls.textInput,
    width: "tiny",
    pattern: patterns.positiveInteger,
    parse: numberParse,
    rangeFilter: true,
    laneMatchOrder: 12
  },
  {
    field: ADDITIONAL_FEE,
    cellUnit: cellUnit.dollar,
    unitLeft: true,
    header: messages.addFee.defaultMessage,
    right: true,
    control: controls.textInput,
    width: "medium",
    pattern: patterns.intSixFractionalTwo,
    parse: numberParse,
    format: round,
    effects: () => [
      TOTAL_COST,
      MARGIN,
      TOTAL_RATE,
      RATE_PER_UNIT,
      FUEL_RATE,
      FLAT_RATE,
      BASE_RATE_PER_UNIT
    ],
    rangeFilter: true
  },
  {
    field: ORIGIN_NOTE,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.origNotes.defaultMessage,
    control: controls.textInput,
    width: "large",
    filter: true,
    left: true,
    pattern: patterns.maxThousandCharacters
  },
  {
    field: DESTINATION_NOTE,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.destNotes.defaultMessage,
    control: controls.textInput,
    width: "large",
    filter: true,
    left: true,
    pattern: patterns.maxThousandCharacters
  },
  {
    field: BID_NOTE,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.bidNotes.defaultMessage,
    control: controls.textInput,
    width: "medium",
    filter: true,
    left: true,
    pattern: patterns.maxThousandCharacters
  },
  {
    field: LAST_EDITED_USER_NAME,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.lastUpdate.defaultMessage,
    control: controls.none,
    width: "medium",
    filter: true,
    left: true,
    laneMatchOrder: 18
  },
  {
    field: HAZMAT,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.hazmat.defaultMessage,
    control: controls.checkbox,
    center: true,
    filter: true
  },
  {
    field: ORIGIN_DROP_TRAILER,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.origDropTrailer.defaultMessage,
    control: controls.checkbox,
    center: true,
    filter: true
  },
  {
    field: DESTINATION_DROP_TRAILER,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.destDropTrailer.defaultMessage,
    control: controls.checkbox,
    center: true,
    filter: true
  },
  {
    field: PERMIT,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.permit.defaultMessage,
    control: controls.checkbox,
    width: "tiny",
    center: true,
    filter: true
  },
  {
    field: TEAM,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.team.defaultMessage,
    control: controls.checkbox,
    width: "tiny",
    center: true,
    filter: true
  },
  {
    field: HRHV,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.hrhv.defaultMessage,
    control: controls.checkbox,
    width: "tiny",
    center: true,
    filter: true
  },
  {
    field: TARGET_RATE,
    cellUnit: cellUnit.dollar,
    unitLeft: true,
    header: messages.targetRate.defaultMessage,
    right: true,
    control: controls.textInput,
    width: "medium",
    pattern: patterns.intSixFractionalTwo,
    parse: numberParse,
    isCoyoteField: true
  },
  {
    field: MINIMUM_RATE,
    cellUnit: cellUnit.dollar,
    unitLeft: true,
    header: messages.minRate.defaultMessage,
    right: true,
    control: controls.textInput,
    width: "medium",
    pattern: patterns.intSixFractionalTwo,
    parse: numberParse,
    isCoyoteField: true
  },
  {
    field: CAPACITY_SOURCE,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.capSource.defaultMessage,
    control: controls.textInput,
    width: "medium",
    filter: true,
    left: true,
    pattern: patterns.maxFiftyCharacters,
    laneMatchOrder: 19,
    isICM: true
  },
  {
    field: INCUMBENT_RATE,
    cellUnit: cellUnit.dollar,
    unitLeft: true,
    header: messages.incmbtRate.defaultMessage,
    right: true,
    control: controls.textInput,
    width: "medium",
    pattern: patterns.intSixFractionalTwo,
    parse: numberParse,
    filter: true,
    isCoyoteField: true
  },
  {
    field: FUEL_SURCHARGE,
    cellUnit: cellUnit.dollar,
    unitLeft: true,
    header: messages.fscValue.defaultMessage,
    right: true,
    control: controls.textInput,
    width: "medium",
    pattern: patterns.intFourFractionalTwo,
    parse: numberParse,
    calculate: lane => {
      if (lane[FUEL_SURCHARGE_TYPE] === fscTypesIds.dollarPerMile) {
        if (
          areNumbers(lane, tlColumnConstants, [FUEL_RATE, SHIPPER_DISTANCE])
        ) {
          lane[FUEL_SURCHARGE] =
            lane[FUEL_RATE] / getShiperMiles(lane, tlColumnConstants);
        }
      } else if (lane[FUEL_SURCHARGE_TYPE] === fscTypesIds.percentOfRate) {
        if (
          areNumbers(lane, tlColumnConstants, [
            FUEL_RATE,
            SHIPPER_DISTANCE,
            RATE_PER_UNIT
          ])
        ) {
          lane[FUEL_SURCHARGE] =
            (-1 -
              lane[RATE_PER_UNIT] /
                (lane[FUEL_RATE] / getShiperMiles(lane, tlColumnConstants) -
                  lane[RATE_PER_UNIT])) *
            100;
        }
      } else if (lane[FUEL_SURCHARGE_TYPE] === fscTypesIds.breakthrough) {
        if (areNumbers(lane, tlColumnConstants, [FUEL_RATE])) {
          lane[FUEL_SURCHARGE] = lane[FUEL_RATE];
        }
      }
    },
    format: round,
    effects: () => [
      TOTAL_COST,
      TOTAL_RATE,
      RATE_PER_UNIT,
      FUEL_RATE,
      MARGIN,
      MARGIN_PERCENTAGE,
      FLAT_RATE,
      BASE_RATE_PER_UNIT
    ]
  },
  {
    field: FUEL_SURCHARGE_TYPE,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.fscType.defaultMessage,
    control: controls.none,
    width: "medium",
    filter: true,
    left: true,
    format: value =>
      fscTypes.find(
        f => (!f.division || f.division === division.coy) && f.value === value
      )?.label
  },
  {
    field: AWARD_RATE,
    cellUnit: cellUnit.dollar,
    unitLeft: true,
    header: messages.awardRate.defaultMessage,
    control: controls.none,
    width: "medium",
    right: true
  },
  {
    field: AWARD_VOLUME,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.awardVol.defaultMessage,
    control: controls.none,
    width: "medium",
    right: true,
    rangeFilter: true
  },
  {
    field: AWARD_TYPE,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.awardType.defaultMessage,
    control: controls.none,
    width: "medium",
    filter: true,
    left: true,
    format: value =>
      !value ? value : awardType.find(f => f.value === value).label
  },
  {
    field: AWARD_RANK,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.awardRank.defaultMessage,
    control: controls.none,
    width: "medium",
    filter: true,
    left: true,
    format: value =>
      !value ? value : awardRank.find(f => f.value === value).label
  },
  {
    field: MX_MILES,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.mxMiles.defaultMessage,
    right: true,
    control: controls.textInput,
    width: "medium",
    pattern: patterns.intFourFractionalOne,
    parse: numberParseForEmptyValue,
    format: roundForNullableField,
    rangeFilter: true
  },
  {
    field: BORDER_CROSSING_CITY,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.bcCity.defaultMessage,
    control: controls.none,
    width: "",
    filter: true,
    left: true
  },
  {
    field: BORDER_CROSSING_STATE,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.bcState.defaultMessage,
    control: controls.none,
    width: "",
    filter: true,
    left: true
  },
  {
    field: BORDER_CROSSING_COUNTRY,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.bcCountry.defaultMessage,
    control: controls.none,
    width: "medium",
    filter: true,
    left: true
  },
  {
    field: MX_COST,
    cellUnit: cellUnit.dollar,
    unitLeft: true,
    header: messages.mxCost.defaultMessage,
    right: true,
    width: "medium",
    control: controls.textInput,
    pattern: patterns.intSixFractionalTwo,
    parse: numberParse,
    effects: () => [
      TOTAL_COST,
      MARGIN,
      TOTAL_RATE,
      RATE_PER_UNIT,
      FUEL_RATE,
      FLAT_RATE,
      BASE_RATE_PER_UNIT
    ]
  },
  {
    field: BORDER_CROSSING_FEE,
    cellUnit: cellUnit.dollar,
    unitLeft: true,
    header: messages.bcFee.defaultMessage,
    right: true,
    width: "medium",
    control: controls.textInput,
    pattern: patterns.intSixFractionalTwo,
    parse: numberParse,
    effects: () => [
      TOTAL_COST,
      MARGIN,
      TOTAL_RATE,
      RATE_PER_UNIT,
      FUEL_RATE,
      FLAT_RATE,
      BASE_RATE_PER_UNIT
    ]
  },
  {
    field: TRANSLOAD_FEE,
    cellUnit: cellUnit.dollar,
    unitLeft: true,
    header: messages.transldFee.defaultMessage,
    right: true,
    width: "medium",
    control: controls.textInput,
    pattern: patterns.intSixFractionalTwo,
    parse: numberParse,
    effects: () => [
      TOTAL_COST,
      MARGIN,
      TOTAL_RATE,
      RATE_PER_UNIT,
      FUEL_RATE,
      FLAT_RATE,
      BASE_RATE_PER_UNIT
    ]
  },
  {
    field: "truckloadLaneId-delete",
    id: "truckloadLaneId",
    header: "",
    control: controls.delete,
    isDraggable: false,
    columnChooserLabel: messages.delete.defaultMessage
  },
  {
    field: BID_START_DATE,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.bidStartDate.defaultMessage,
    control: controls.none,
    width: "medium",
    filter: true,
    left: true,
    format: formatDate,
    laneMatchOrder: 6,
    onlyLaneMatch: true
  },
  {
    field: BID_END_DATE,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.bidEndDate.defaultMessage,
    control: controls.none,
    width: "medium",
    filter: true,
    left: true,
    format: formatDate,
    laneMatchOrder: 7,
    onlyLaneMatch: true
  },
  {
    field: SHIPPER_NAME,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.shipper.defaultMessage,
    control: controls.none,
    width: "medium",
    filter: true,
    left: true,
    laneMatchOrder: 8,
    onlyLaneMatch: true
  },
  {
    field: BID_NAME,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.bidName.defaultMessage,
    control: controls.none,
    width: "medium",
    filter: true,
    left: true,
    laneMatchOrder: 9,
    onlyLaneMatch: true
  },
  {
    field: BID_STATUS,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.status.defaultMessage,
    control: controls.none,
    width: "medium",
    filter: true,
    left: true,
    format: value => value && rfqStatusTypes.find(x => x.value === value).label,
    laneMatchOrder: 10,
    onlyLaneMatch: true
  },
  {
    field: PRICING_CONFIDENCE,
    control: controls.hide,
    header: "",
    format: value =>
      !value
        ? value
        : confidenceTypeColorCodes.find(f => f.value === value).label
  }
];

export default tlColumns;
